/**
 * Acceso del alumno al contenido de pago.
 *
 * Mira dos cosas y basta con una:
 *   - user_subscription con estado activo o en prueba
 *   - course_enrollment, que cubre altas manuales y cortesias
 *
 * Las columnas siguen el esquema real de las tablas: enrollment no
 * tiene id (su clave es user_id + course_slug) y la suscripcion no
 * tiene trial_end, el periodo de prueba se refleja en el estado.
 */

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { getSupabase, isSupabaseEnabled } from '@/lib/supabase'

export interface SubscriptionStatus {
  hasAccess: boolean
  isTrialing: boolean
  /** Dias que quedan de prueba. Cero si no esta en prueba. */
  trialDaysLeft: number
  status: 'active' | 'trialing' | 'none' | 'loading'
}

const NO_ACCESS: SubscriptionStatus = {
  hasAccess: false,
  isTrialing: false,
  trialDaysLeft: 0,
  status: 'none',
}

export function useSubscriptionStatus(): SubscriptionStatus {
  const { user } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: ['access-status', user?.id],
    queryFn: async () => {
      if (!isSupabaseEnabled() || !user?.id) return { sub: null, enrolled: false }
      const supabase = getSupabase()
      if (!supabase) return { sub: null, enrolled: false }

      const [subRes, enrolRes, purchaseRes, seatRes] = await Promise.all([
        supabase
          .from('user_subscription')
          .select('status, current_period_end, cancel_at_period_end')
          .eq('user_id', user.id)
          .in('status', ['active', 'trialing'])
          .order('current_period_end', { ascending: false })
          .limit(1),
        supabase
          .from('course_enrollment')
          .select('course_slug, expires_at')
          .eq('user_id', user.id)
          .limit(20),
        supabase
          .from('course_purchase')
          .select('course_slug, expires_at')
          .eq('user_id', user.id)
          .limit(20),
        // Asiento pagado por su empresa
        supabase.rpc('my_seat_organization'),
      ])

      if (subRes.error) console.info('[access] subscription:', subRes.error.message)
      if (enrolRes.error) console.info('[access] enrollment:', enrolRes.error.message)
      if (purchaseRes.error) console.info('[access] purchase:', purchaseRes.error.message)

      const now = Date.now()

      /** Vale mientras no tenga fecha de caducidad o siga vigente. */
      const vigente = (r: { expires_at: unknown }) => {
        const expires = r.expires_at as string | null
        return !expires || new Date(expires).getTime() > now
      }

      // La funcion ya filtra los caducados, asi que basta con que
      // devuelva algo.
      const conAsiento = Array.isArray(seatRes.data) && seatRes.data.length > 0

      const enrolled =
        (enrolRes.data ?? []).some(vigente) ||
        (purchaseRes.data ?? []).some(vigente) ||
        conAsiento

      const raw = subRes.data?.[0] ?? null

      // Con margen de un dia: entre que Stripe cobra y avisa pueden
      // pasar horas, y no se puede echar a quien acaba de pagar.
      const GRACIA_MS = 86_400_000
      const vigenteSub =
        raw?.current_period_end
          ? new Date(raw.current_period_end as string).getTime() + GRACIA_MS > now
          : false

      const sub = vigenteSub ? raw : null
      const trialDaysLeft = sub?.status === 'trialing' && sub.current_period_end
        ? Math.max(0, Math.ceil(
            (new Date(sub.current_period_end as string).getTime() - now) / 86_400_000,
          ))
        : 0

      return { sub, enrolled, trialDaysLeft }
    },
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return { ...NO_ACCESS, status: 'loading' }
  }

  const sub = data?.sub as
    | { status: string; current_period_end: string | null }
    | null
    | undefined

  if (sub) {
    const isTrialing = sub.status === 'trialing'

    return {
      hasAccess: true,
      isTrialing,
      trialDaysLeft: data?.trialDaysLeft ?? 0,
      status: isTrialing ? 'trialing' : 'active',
    }
  }

  if (data?.enrolled) {
    return { hasAccess: true, isTrialing: false, trialDaysLeft: 0, status: 'active' }
  }

  return NO_ACCESS
}
